import type React from 'react';
import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';
import { useStockList } from '../hooks/useStockList';
import type { Stock } from '../types/stock';

interface StockContextProps {
  stocks: Stock[];
  loading: boolean;
  error: Error | null;
  totalCount: number | undefined;
  isComplete: boolean;
  refetch: () => void;
  isRefetching: boolean;
  isLoadingMore: boolean; // Loading additional data in background
  hasPartialData: boolean; // Has some data but still loading more
}

const StockContext = createContext<StockContextProps | null>(null);

export const useStocks = () => {
  const context = useContext(StockContext);
  if (!context) {
    throw new Error('useStocks must be used within a StockProvider');
  }
  return context;
};

interface StockProviderProps {
  children: ReactNode;
}

export const StockProvider: React.FC<StockProviderProps> = ({ children }) => {
  const stockQuery = useStockList();

  const contextValue: StockContextProps = useMemo(
    () => ({
      stocks: stockQuery.stocks,
      loading: stockQuery.isLoading,
      error: stockQuery.error,
      totalCount: stockQuery.totalCount,
      isComplete: stockQuery.isComplete,
      refetch: stockQuery.refetch,
      isRefetching: stockQuery.isRefetching,
      isLoadingMore: stockQuery.isLoadingMore || false,
      hasPartialData: stockQuery.hasPartialData || false,
    }),
    [
      stockQuery.stocks,
      stockQuery.stocks.length,
      stockQuery.isLoading,
      stockQuery.error,
      stockQuery.totalCount,
      stockQuery.isComplete,
      stockQuery.refetch,
      stockQuery.isRefetching,
      stockQuery,
    ]
  );

  return <StockContext.Provider value={contextValue}>{children}</StockContext.Provider>;
};
